<template>
  <nav class="navigation">
    <div class="nav-container">
      <div class="nav-left">
          <router-link to="/" class="nav-logo">
              <img src="/img/logo_quare.svg" alt="Invoice Scan" class="logo-text" style="height: 36px" />
          </router-link>
          <router-link to="/" class="nav-link" active-class="active" :exact="true">
              <font-awesome-icon icon="fa-solid fa-file-lines" />
              {{ $t('nav.dashboard') }}
          </router-link>
      </div>

      <div class="nav-center">
          <router-link to="/companies" class="nav-link" active-class="active">
              <svg style="width: 1em; height: 1em;" viewBox="0 0 384 512" fill="currentColor">
                  <path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-384c0-35.3-28.7-64-64-64L64 0zM176 352l32 0c17.7 0 32 14.3 32 32l0 80-96 0 0-80c0-17.7 14.3-32 32-32zM96 112c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32zM240 96l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zM96 240c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32zm144-16l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16z"/>
              </svg>
              {{ $t('nav.companies') }}
          </router-link>
      </div>

      <div class="nav-right">
        <LanguageSelector />
        <div class="user-info">
          <span class="user-name">{{ userName }}</span>
        </div>
        <router-link to="/profile" class="btn-icon" :title="$t('nav.profile')">
          <font-awesome-icon icon="fa-solid fa-user" />
        </router-link>
        <router-link to="/subscription" class="btn-icon" :title="$t('profile.subscription')">
          <font-awesome-icon icon="fa-solid fa-credit-card" />
        </router-link>
        <button @click="handleLogout" class="btn-icon" :title="$t('auth.logout')">
          <font-awesome-icon icon="fa-solid fa-right-from-bracket" />
        </button>
      </div>

      <!-- Hamburger Menu Button (Mobile Only) -->
      <button @click="toggleMenu" class="hamburger-btn">
        <font-awesome-icon :icon="menuOpen ? 'xmark' : 'bars'" />
      </button>
    </div>

    <!-- Mobile Menu Overlay -->
    <div v-if="menuOpen" class="mobile-menu-overlay" @click="closeMenu"></div>
    
    <!-- Mobile Menu -->
    <div class="mobile-menu" :class="{ open: menuOpen }">
      <div class="mobile-menu-header">
        <div class="user-info-mobile">
          <font-awesome-icon icon="fa-solid fa-user-circle" class="user-icon" />
          <span class="user-name">{{ userName }}</span>
        </div>
      </div>
      
      <div class="mobile-menu-items">
        <LanguageSelector />
        
        <router-link to="/profile" class="mobile-menu-item" @click="closeMenu">
          <font-awesome-icon icon="fa-solid fa-user" />
          <span>{{ $t('nav.profile') }}</span>
        </router-link>
        
        <router-link to="/subscription" class="mobile-menu-item" @click="closeMenu">
          <font-awesome-icon icon="fa-solid fa-credit-card" />
          <span>{{ $t('profile.subscription') }}</span>
        </router-link>
        
        <button @click="handleLogout" class="mobile-menu-item logout-btn">
          <font-awesome-icon icon="fa-solid fa-right-from-bracket" />
          <span>{{ $t('auth.logout') }}</span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import api from '@/utils/api';
import LanguageSelector from '@/components/LanguageSelector.vue';

const router = useRouter();
const authStore = useAuthStore();

const userName = computed(() => authStore.user?.name || 'User');
const menuOpen = ref(false);

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const handleLogout = async () => {
  closeMenu();
  try {
    await authStore.logout();
  } catch (error) {
    console.error('Logout failed:', error);
  }
};
</script>

<style scoped>
.navigation {
  background: rgba(244, 243, 248, 0.8);
  -webkit-backdrop-filter: saturate(160%) blur(16px);
  backdrop-filter: saturate(160%) blur(16px);
  border-bottom: 1px solid rgba(31, 41, 51, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: none;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em;
}
.navigation a, .navigation button, .navigation select { font-family: inherit; }

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

.nav-left {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.nav-logo:hover {
  opacity: 0.8;
}

.logo-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.logo-text {
  height: 20px;
  flex-shrink: 0;
}

.nav-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--gray);
  text-decoration: none;
  transition: all 0.2s ease;
}

.nav-link:hover {
  background: var(--light-gray);
  color: var(--dark);
}

.nav-link {
  border-radius: 11px;
}
.nav-link.active {
  background: rgba(43, 130, 240, 0.1);
  color: #2b82f0;
  box-shadow: none;
}

.nav-link svg {
  display: none;
}

.nav-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info {
  padding: 9px 16px;
  background: rgba(43, 130, 240, 0.1);
  border-radius: 11px;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #2b82f0;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid rgba(31, 41, 51, 0.08);
  border-radius: 11px;
  color: var(--gray);
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-icon:hover {
  background: #fff;
  border-color: #2b82f0;
  color: #2b82f0;
  transform: translateY(-1px);
}

.btn-icon svg {
  flex-shrink: 0;
}

button.btn-icon:hover {
  background: var(--danger-light);
  border-color: var(--danger-lighter);
  color: var(--danger);
}

/* Hamburger Button */
.hamburger-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid rgba(31, 41, 51, 0.08);
  border-radius: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  color: var(--dark);
}

.hamburger-btn:hover {
  background: #fff;
  border-color: #2b82f0;
  color: #2b82f0;
}

/* Mobile Menu Overlay */
.mobile-menu-overlay {
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  /* Use an explicit viewport-based height instead of top+bottom: the nav is the
     containing block for this fixed element, so bottom:0 collapsed it to ~0px and
     nothing got dimmed. vh is relative to the viewport regardless. */
  height: calc(100vh - 60px);
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

/* Mobile Menu */
.mobile-menu {
  display: none;
  position: fixed;
  top: 60px;
  right: 0;
  width: 280px;
  max-width: 85vw;
  height: calc(100vh - 60px);
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  /* Hide off-screen by the panel's OWN width, so it is reliably hidden even when a
     transformed ancestor changes the containing block (right: -100% was not).
     Also visibility:hidden so a scrollbar gutter can never leave an edge peeking. */
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.3s ease, visibility 0s linear 0.3s;
  z-index: 999;
  overflow-y: auto;
}

.mobile-menu.open {
  transform: translateX(0);
  visibility: visible;
  transition: transform 0.3s ease, visibility 0s linear 0s;
}

.mobile-menu-header {
  padding: 20px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(135deg, var(--primary) 0%, #3b82f6 100%);
}

.user-info-mobile {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.user-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.user-info-mobile .user-name {
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.mobile-menu-items {
  padding: 16px 0;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.mobile-menu-item:hover {
  background: var(--light-gray);
}

.mobile-menu-item svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--gray);
}

.mobile-menu-item.router-link-active {
  background: var(--primary-light);
  color: var(--primary);
}

.mobile-menu-item.router-link-active svg {
  color: var(--primary);
}

.logout-btn {
  margin-top: 8px;
  border-top: 1px solid var(--border);
  padding-top: 24px;
  color: var(--danger);
}

.logout-btn svg {
  color: var(--danger);
}

.logout-btn:hover {
  background: var(--danger-light);
}

/* Responsive */
@media (max-width: 768px) {
  .nav-container {
    padding: 0 12px;
    height: 60px;
  }

  .nav-left {
    flex: 0 0 auto;
    gap: 16px;
  }

  .logo-icon {
    width: 28px;
    height: 28px;
  }

  .logo-text {
    height: 18px;
  }

  .nav-logo {
    font-size: 16px;
  }

  .nav-center {
    flex: 1;
    justify-content: flex-start;
    gap: 4px;
    margin-left: 12px;
  }

  .nav-link {
    padding: 8px 12px;
    font-size: 13px;
  }

  .nav-right {
    display: none;
  }

  .hamburger-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 33px !important;
    height: 33px !important;
    padding: 0 !important;
    font-size: 16px !important;
  }

  .mobile-menu-overlay {
    display: block;
  }

  .mobile-menu {
    display: block;
  }

  .btn-icon {
    width: 36px;
    height: 36px;
  }

  .btn-icon svg {
    width: 18px;
    height: 18px;
  }
}
</style>
